import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers';
import CommonFilterSection from '../../../../components/common/CommonFilterSection';
import { fetchVendorListCached, getVendorListCached } from '../../../../utils/cache/vendorCache';
import { fetchManagerListCached, getManagerListCached } from '../../../../utils/cache/managerCache';
import {
    FilterSection as StyledFilterSection,
    DateFilterContainer,
    SectionTitle,
    StyledDatePicker,
    ButtonContainer
} from '../styles';

const FilterSection = ({
    searchParams,
    handleParamChange,
    handleSearch,
    handleReset,
    isLoading
}) => {
    const { t } = useTranslation();
    const [vendorOptions, setVendorOptions] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);

    useEffect(() => {
        const hydrateOptions = async () => {
            let vendors = getVendorListCached() || [];
            let managers = getManagerListCached() || [];

            const shouldFetch = [vendors, managers].some((arr) => !arr || arr.length === 0);
            if (shouldFetch) {
                await Promise.all([
                    fetchVendorListCached(false),
                    fetchManagerListCached(false)
                ]);
                vendors = getVendorListCached() || [];
                managers = getManagerListCached() || [];
            }

            const vendorByName = new Map();
            vendors.forEach((v) => {
                const name = String(v?.partner_name || v?.vendor_name || v?.company_name || '').trim();
                if (!name || vendorByName.has(name)) return;
                const pid = v?.partner_id ?? v?.vendor_id ?? v?.company_id;
                vendorByName.set(name, pid);
            });
            const vendorOpts = [...vendorByName.keys()]
                .sort((a, b) => a.localeCompare(b))
                .map((name) => ({ value: name, label: name, partnerId: vendorByName.get(name) }));

            const managerLabels = [...new Set(
                managers
                    .map((m) => m?.manager_name || m?.name || '')
                    .map((s) => String(s).trim())
                    .filter(Boolean)
            )].sort((a, b) => a.localeCompare(b));

            setVendorOptions(vendorOpts);
            setManagerOptions(managerLabels.map((label) => ({ value: label, label })));
        };

        hydrateOptions().catch(() => {});
    }, []);

    // 날짜 필드 설정
    const dateFields = [
        {
            key: 'startDate',
            component: <StyledDatePicker />,
            props: {}
        },
        {
            key: 'endDate',
            component: <StyledDatePicker />,
            props: {}
        }
    ];

    // 검색 필드 설정 (테이블 컬럼 순서에 맞게 정렬, 첫 번째 컬럼 제외)
    const searchFields = useMemo(() => ([
        {
            name: 'customer_name',
            label: t('orders.columns.custom'),
            type: 'autocomplete',
            options: vendorOptions,
            freeSolo: true,
            xs: 12,
            sm: 6,
            md: 3
        },
        {
            name: 'manager',
            label: t('orders.columns.name'),
            type: 'autocomplete',
            options: managerOptions,
            freeSolo: true,
            xs: 12,
            sm: 6,
            md: 3
        },
        {
            name: 'invoice_no',
            label: t('invoice.invoiceNo'),
            type: 'text',
            xs: 12,
            sm: 6,
            md: 3
        },
        {
            name: 'due_date',
            label: t('invoice.dueDate'),
            type: 'text',
            xs: 12,
            sm: 6,
            md: 3
        },
        {
            name: 'status',
            label: t('invoice.status'),
            type: 'select',
            xs: 12,
            sm: 6,
            md: 3,
            options: [
                { value: '', label: t('common.all') },
                { value: 'issued', label: t('invoice.statusOptions.issued') },
                { value: 'canceled', label: t('invoice.statusOptions.canceled') },
                { value: 'completed', label: t('invoice.statusOptions.completed') }
            ]
        },
        {
            name: 'status_account_receivable',
            label: t('invoice.columns.paymentStatus'),
            type: 'select',
            xs: 12,
            sm: 6,
            md: 3,
            options: [
                { value: '', label: t('common.all') },
                { value: 'paid', label: t('invoice.statusOptions.paid') },
                { value: 'unpaid', label: t('invoice.statusOptions.unpaid') },
                { value: 'partial_paid', label: t('invoice.statusOptions.partial_paid') },
                { value: 'overdue', label: t('invoice.statusOptions.overdue') }
            ]
        }
    ]), [t, vendorOptions, managerOptions]);

    return (
        <CommonFilterSection
            title={t('invoice.fields.searchConditions')}
            searchParams={searchParams}
            handleParamChange={handleParamChange}
            handleSearch={handleSearch}
            handleReset={handleReset}
            isLoading={isLoading}
            dateFields={dateFields}
            searchFields={searchFields}
            showDateFilter={true}
            showButtons={true}
            StyledFilterSection={StyledFilterSection}
            StyledSectionTitle={SectionTitle}
            StyledDateFilterContainer={DateFilterContainer}
            StyledButtonContainer={ButtonContainer}
        />
    );
};

export default FilterSection; 